import { useEffect, useRef, useState } from 'react';
import type { CheckResult, DomainStatus } from '@domaintriage/shared';
import { ExportButtons } from './ExportButtons';

interface ShortlistViewProps {
  results: CheckResult[];
  shortlist: Set<string>;
  onRemove: (domain: string) => void;
  onClear: () => void;
  onClose: () => void;
  onSelectDomain: (domain: string) => void;
}

function StatusDot({ status }: { status: DomainStatus }) {
  const color =
    status === 'available'
      ? 'bg-green-500'
      : status === 'registered'
        ? 'bg-red-500'
        : 'bg-amber-500';
  const label =
    status === 'available' ? 'Available' : status === 'registered' ? 'Registered' : 'Unknown';

  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-full ${color}`} aria-hidden="true" />
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </span>
  );
}

export function ShortlistView({
  results,
  shortlist,
  onRemove,
  onClear,
  onClose,
  onSelectDomain,
}: ShortlistViewProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const shortlistedResults = results.filter((r) => shortlist.has(r.domain));
  const shortlistedDomains = [...shortlist].filter(
    (d) => !shortlistedResults.some((r) => r.domain === d),
  );

  // Collect notes for export
  const notes = new Map<string, string>();
  for (const domain of shortlist) {
    try {
      const note = localStorage.getItem(`domaintriage-note-${domain}`);
      if (note) notes.set(domain, note);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    closeRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleClear() {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    onClear();
    setConfirmClear(false);
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Shortlist"
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col overflow-y-auto bg-white shadow-2xl sm:rounded-l-xl dark:bg-gray-900"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white/95 px-6 py-4 backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Shortlist
            {shortlist.size > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({shortlist.size})
              </span>
            )}
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close shortlist"
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        <div className="flex-1 p-6">
          {shortlist.size === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600"
                aria-hidden="true"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">
                No domains shortlisted yet.
              </p>
              <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
                Click the star icon on any result to add it here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Domain list */}
              <ul className="space-y-2">
                {shortlistedResults.map((result) => (
                  <li
                    key={result.domain}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    <button
                      type="button"
                      onClick={() => onSelectDomain(result.domain)}
                      className="min-w-0 flex-1 text-left focus:outline-none focus-visible:underline"
                    >
                      <p className="truncate font-mono text-sm font-medium text-gray-900 dark:text-white">
                        {result.domain}
                      </p>
                      <StatusDot status={result.status} />
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemove(result.domain)}
                      aria-label={`Remove ${result.domain} from shortlist`}
                      className="ml-2 shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-red-400"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                      </svg>
                    </button>
                  </li>
                ))}
                {/* Domains in shortlist but not in current results */}
                {shortlistedDomains.map((domain) => (
                  <li
                    key={domain}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-mono text-sm font-medium text-gray-900 dark:text-white">
                        {domain}
                      </p>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Not in current results
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemove(domain)}
                      aria-label={`Remove ${domain} from shortlist`}
                      className="ml-2 shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-red-400"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Clear all */}
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleClear}
                  className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    confirmClear
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950'
                  }`}
                >
                  {confirmClear ? 'Confirm Clear All' : 'Clear All'}
                </button>
                {confirmClear && (
                  <button
                    type="button"
                    onClick={() => setConfirmClear(false)}
                    className="ml-2 rounded-lg px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Export */}
              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Export Shortlist
                </h3>
                <ExportButtons
                  results={shortlistedResults}
                  notes={notes.size > 0 ? notes : undefined}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
